import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

// Save As — clone a model into a new name, optionally a different workspace,
// and optionally reassign / rename its data source, all in one step.
export default function SaveAsModelDialog({ open, onOpenChange, model, onCloned }) {
  const [name, setName] = useState('');
  const [workspaceId, setWorkspaceId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [workspaces, setWorkspaces] = useState([]);
  const [sources, setSources] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(`Copy of ${model?.name || 'Model'}`);
    setWorkspaceId(model?.workspace_id || '');
    setSourceId(model?.source_id || '');
    setSourceName(model?.source_name || '');
    (async () => {
      try {
        const [ws, ds] = await Promise.all([api.getCloneWorkspaces().catch(() => []), api.getDataSources().catch(() => [])]);
        setWorkspaces(Array.isArray(ws) ? ws : (ws?.workspaces || []));
        setSources(Array.isArray(ds) ? ds : (ds?.sources || []));
      } catch { /* non-fatal */ }
    })();
  }, [open, model]);

  const submit = async () => {
    if (!name.trim()) { toast.error('Give the copy a name'); return; }
    setSaving(true);
    try {
      const payload = { name: name.trim() };
      if (workspaceId && workspaceId !== model?.workspace_id) payload.target_workspace_id = workspaceId;
      if (sourceId && sourceId !== model?.source_id) payload.source_id = sourceId;
      if (sourceName && sourceName !== model?.source_name) payload.source_name = sourceName.trim();
      const res = await api.cloneSemanticModel(model.model_id, payload);
      toast.success(`Copied to "${res?.model?.name}" (${res.entities} tables, ${res.measures} measures)`);
      onOpenChange(false);
      onCloned && onCloned(res);
    } catch (e) { toast.error(e.message || 'Save As failed'); }
    setSaving(false);
  };

  const crossWorkspace = workspaceId && workspaceId !== model?.workspace_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="save-as-dialog">
        <DialogHeader>
          <DialogTitle>Save a copy of this model</DialogTitle>
          <DialogDescription>Duplicates all tables, joins and measures. The copy starts as a draft.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-muted-foreground">New model name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 text-sm" data-testid="save-as-name" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Workspace</Label>
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              <SelectTrigger className="mt-1 text-sm" data-testid="save-as-workspace"><SelectValue placeholder="Choose a workspace" /></SelectTrigger>
              <SelectContent>
                {workspaces.map(w => (
                  <SelectItem key={w.workspace_id} value={w.workspace_id} data-testid={`save-as-ws-${w.workspace_id}`}>
                    {w.name}{w.workspace_id === model?.workspace_id ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {crossWorkspace && <p className="text-[11px] text-amber-400 mt-1">The copy will be created in a different workspace.</p>}
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Data source</Label>
            <Select value={sourceId} onValueChange={(v) => { setSourceId(v); const s = sources.find(x => x.source_id === v); if (s) setSourceName(s.name); }}>
              <SelectTrigger className="mt-1 text-sm" data-testid="save-as-source"><SelectValue placeholder="Keep current data source" /></SelectTrigger>
              <SelectContent>
                {sources.map(s => (
                  <SelectItem key={s.source_id} value={s.source_id} data-testid={`save-as-src-${s.source_id}`}>
                    {s.name}{s.source_id === model?.source_id ? ' (current)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Data source label (rename on the copy)</Label>
            <Input value={sourceName} onChange={e => setSourceName(e.target.value)} className="mt-1 text-sm" placeholder="Display name for the source" data-testid="save-as-source-name" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="text-xs">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={saving || !name.trim()} className="text-xs" data-testid="save-as-submit">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Copy className="w-3.5 h-3.5 mr-1" />} Save copy
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
